import React from "react";
import { Flame, Sparkles, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const YANTRAS = [
  { name: "Shri Yantra", benefit: "Wealth & abundance. Devi Lakshmi's seat.", mantra: "ॐ श्रीं महालक्ष्म्यै नमः", color: "from-amber-500/20 to-red-500/20" },
  { name: "Kuber Yantra", benefit: "Financial stability & business growth.", mantra: "ॐ यक्षाय कुबेराय वैश्रवणाय…", color: "from-yellow-500/20 to-orange-500/20" },
  { name: "Sarasvati Yantra", benefit: "Learning, wisdom & artistic mastery.", mantra: "ॐ ऐं सरस्वत्यै नमः", color: "from-cyan-500/20 to-blue-500/20" },
  { name: "Bagalamukhi Yantra", benefit: "Victory over enemies & court cases.", mantra: "ॐ ह्लीं बगलामुखी सर्वदुष्टानाम्…", color: "from-yellow-500/20 to-amber-600/20" },
  { name: "Maha Mrityunjaya Yantra", benefit: "Healing & protection from death.", mantra: "ॐ त्र्यम्बकं यजामहे…", color: "from-red-500/20 to-purple-500/20" },
  { name: "Ganesha Yantra", benefit: "Removes obstacles, brings success.", mantra: "ॐ गं गणपतये नमः", color: "from-orange-500/20 to-red-500/20" },
];

const MANTRAS = [
  { name: "Gayatri Mantra", purpose: "Wisdom & inner light", text: "ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात्" },
  { name: "Mahamrityunjaya", purpose: "Healing & longevity", text: "ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम् उर्वारुकमिव बन्धनान् मृत्योर्मुक्षीय मामृतात्" },
  { name: "Ganesh Mantra", purpose: "Obstacle removal", text: "वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा" },
];

const TANTRA_REMEDIES = [
  { title: "Grah Shanti", desc: "Nullify malefic planetary influences via specific pooja & yagya.", price: "₹5,100+" },
  { title: "Manglik Dosh Nivaran", desc: "Ritual to reduce Mars affliction before marriage.", price: "₹3,100+" },
  { title: "Pitra Dosh", desc: "Ancestor appeasement via Shraadh & Narayan Bali.", price: "₹7,500+" },
  { title: "Kaal Sarp Dosh", desc: "Rahu-Ketu axis remedy at Trimbakeshwar.", price: "₹11,000+" },
];

export default function YantraMantraTantraService() {
  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-orange-500/20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0a05] via-[#0f0705] to-[#050507]" />
        <div className="absolute inset-0 starfield opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="text-xs uppercase tracking-[0.35em] text-[#FF5A36] mb-4">Yantra · Mantra · Tantra</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-3xl">
            <span className="italic gold-gradient">Sacred geometry.</span><br/>
            Sound. Ritual.
          </h1>
          <p className="mt-6 max-w-xl text-[#A0A4AB]">
            The three-fold path of Tantra — visual (yantra), auditory (mantra) and ritual (tantra) technology
            to shift energy in your life.
          </p>
        </div>
      </div>

      {/* Yantras */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">01 · Yantra</div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Six power <span className="italic gold-gradient">yantras.</span></h2>
        <div className="grid md:grid-cols-3 gap-4">
          {YANTRAS.map((y, i) => (
            <div key={y.name} data-testid={`yantra-${i}`} className={`relative p-8 rounded-2xl bg-gradient-to-br ${y.color} border border-white/10 overflow-hidden group hover:border-[#D4AF37]/50 transition`}>
              <div className="absolute -right-8 -top-8 w-32 h-32 border border-[#D4AF37]/30 rotate-45 group-hover:rotate-90 transition-transform duration-1000" />
              <div className="absolute -right-2 -top-2 w-20 h-20 border border-[#D4AF37]/40 rotate-45 group-hover:-rotate-45 transition-transform duration-1000" />
              <Flame className="text-[#FF5A36]" size={24} strokeWidth={1.3}/>
              <div className="font-serif text-2xl mt-4">{y.name}</div>
              <p className="text-sm text-[#A0A4AB] mt-2">{y.benefit}</p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Beej Mantra</div>
                <div className="font-deva text-sm text-[#EAE6DF]">{y.mantra}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mantras */}
      <div className="border-y border-white/10 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">02 · Mantra</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Timeless <span className="italic gold-gradient">sound vibrations.</span></h2>
          <div className="space-y-4">
            {MANTRAS.map((m, i) => (
              <div key={i} className="p-8 rounded-xl bg-[#050507] border border-white/10 grid md:grid-cols-4 gap-6 items-center">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{m.purpose}</div>
                  <div className="font-serif text-2xl mt-1">{m.name}</div>
                </div>
                <div className="md:col-span-3 font-deva text-lg text-[#EAE6DF] leading-relaxed">
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tantric remedies */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#FF5A36] mb-3">03 · Tantra</div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Ritual <span className="italic gold-gradient">remedies</span> for stubborn doshas.</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {TANTRA_REMEDIES.map((r, i) => (
            <div key={i} className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#FF5A36]/50 transition">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-2xl">{r.title}</div>
                  <p className="text-sm text-[#A0A4AB] mt-2 leading-relaxed">{r.desc}</p>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">from</div>
                  <div className="font-serif text-xl text-[#FF5A36]">{r.price}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/astrologers" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF5A36] text-white font-medium hover:bg-[#FF7A5E] transition">
            Consult a Tantra Guru <ArrowUpRight size={14}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
